// The controls in the header: the named-view picker, Copy link and the audio
// start and stop button.

import type { ViewStore } from "../../state/store";
import type { AudioSession } from "./audio-session";
import { el } from "./dom";
import { strings } from "./strings";

export interface HeaderContext {
  store: ViewStore;
  audio: AudioSession;
  toggleAudio(): void;
}

export function mountHeaderControls(root: HTMLElement, ctx: HeaderContext): () => void {
  const { store, audio } = ctx;

  const pickerId = "ctl-view";
  const picker = el("select", { id: pickerId, "data-control": "view" });
  picker.append(el("option", { value: "" }, strings.views.none));
  for (const view of store.views) picker.append(el("option", { value: view.id, title: view.description }, view.title));
  picker.addEventListener("change", () => {
    if (picker.value === "") store.set({ view: null });
    else for (const issue of store.applyView(picker.value)) console.warn(`view: ${issue}`);
  });

  const copy = el("button", { type: "button", class: "ctl-link", "data-control": "copy-link" }, strings.link.copy);
  const copyStatus = el("span", { class: "ctl-status", role: "status" });
  copy.addEventListener("click", () => {
    navigator.clipboard
      .writeText(window.location.href)
      .then(() => (copyStatus.textContent = strings.link.copied))
      .catch(() => (copyStatus.textContent = strings.link.failed));
  });

  const audioButton = el("button", {
    type: "button",
    class: "ctl-audio",
    "data-control": "audio",
    "aria-keyshortcuts": "A",
    title: strings.audio.hint,
  });
  audioButton.addEventListener("click", () => ctx.toggleAudio());

  root.append(
    el("div", { class: "header-view" }, el("label", { for: pickerId }, strings.views.label), picker),
    el("div", { class: "header-link" }, copy, copyStatus),
    audioButton,
  );

  const render = (): void => {
    picker.value = store.state.view ?? "";
    const status = audio.status;
    audioButton.dataset["status"] = status;
    audioButton.textContent =
      status === "on"
        ? strings.audio.stop
        : status === "starting"
          ? strings.audio.starting
          : status === "failed"
            ? strings.audio.failed
            : strings.audio.start;
    audioButton.setAttribute("aria-pressed", String(status === "on"));
    audioButton.disabled = status === "starting";
  };
  const offs = [store.subscribe(render), audio.onStatus(render)];
  render();
  return () => {
    for (const off of offs) off();
  };
}
